import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  Center,
  Flex,
  HStack,
  Icon,
  Select,
  SimpleGrid,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useColorModeValue,
  useBreakpointValue,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import {
  FaArrowLeft,
  FaHistory,
} from "react-icons/fa";
import {
  ChevronRightIcon,
} from "@chakra-ui/icons";
import useAxios from "axios-hooks";
import { useSetRecoilState } from "recoil";
import { toastState } from "atoms/toastState";
import logger from "infra/logger";
import {
  PAYOUT_CONFIG_FIELDS,
  PAYOUT_CONFIG_GROUPS,
} from "../variables/payoutConfigFields";

const MotionBox = motion(Box);
const MotionTr = motion(Tr);

// GlassMorphismCard — mirrors LocationsTable
const GlassMorphismCard = ({ children, ...rest }) => {
  const bgColor = useColorModeValue(
    "rgba(255, 255, 255, 0.8)",
    "rgba(26, 32, 44, 0.8)",
  );
  const borderColor = useColorModeValue(
    "rgba(255, 255, 255, 0.5)",
    "rgba(255, 255, 255, 0.1)",
  );
  return (
    <Box
      bg={bgColor}
      backdropFilter="blur(10px)"
      borderRadius="2xl"
      boxShadow="xl"
      border="1px solid"
      borderColor={borderColor}
      overflow="hidden"
      transition="all 0.3s ease"
      _hover={{ boxShadow: "2xl" }}
      {...rest}
    >
      {children}
    </Box>
  );
};

const formatValue = (field, value) => {
  if (value === null || value === undefined || value === "") {
    return "—";
  }
  const num = Number(value).toFixed(2);
  if (field.unit === "%") return `${num}%`;
  if (field.unit === "₹") return `₹${num}`;
  return num;
};

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const PayoutConfigHistory = () => {
  const navigate = useNavigate();
  const { stationId } = useParams();
  const setToast = useSetRecoilState(toastState);
  const [history, setHistory] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // Year options: current year and previous 2 years
  const YEAR_OPTIONS = (() => {
    const currentYear = new Date().getFullYear();
    return [currentYear, currentYear - 1, currentYear - 2];
  })();

  const textColor = useColorModeValue("gray.700", "white");
  const subTextColor = useColorModeValue("gray.500", "gray.400");
  const bgColor = useColorModeValue("gray.50", "gray.900");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");
  const hoverBg = useColorModeValue("gray.50", "gray.700");
  const theadBgColor = useColorModeValue("gray.50", "gray.800");
  const cardBorderColor = useColorModeValue("gray.200", "whiteAlpha.100");
  const mobileFieldBg = useColorModeValue("gray.50", "whiteAlpha.50");
  const inputBg = useColorModeValue("white", "whiteAlpha.100");
  const themeColor = "#f57d36";

  // Responsive: show table on lg+ screens, cards on mobile/tablet
  const isDesktop = useBreakpointValue({ base: false, lg: true });

  const [{ loading }, fetchHistory] = useAxios(
    {
      method: "GET",
      url: `/api/stations/payoutConfig/${stationId}/history`,
    },
    { manual: true },
  );

  const loadHistory = useCallback(async () => {
    try {
      const response = await fetchHistory();
      const data = response.data?.data || [];
      setHistory(data);
    } catch (error) {
      logger.error("Error fetching payout config history:", error);
      setToast({
        isOpen: true,
        title: "Error fetching history",
        status: "error",
        duration: 5000,
      });
    }
  }, [fetchHistory, setToast]);

  useEffect(() => {
    if (stationId) {
      loadHistory();
    }
  }, [stationId, loadHistory]);

  const currentMonth = new Date().getMonth() + 1;
  const currentYear = new Date().getFullYear();

  // Filter history by selected year
  const filteredHistory = useMemo(() => {
    return history.filter((entry) => entry.Year === selectedYear);
  }, [history, selectedYear]);

  return (
    <Box
      px={{ base: 4, md: 6 }}
      bg={bgColor}
      minH="100vh"
      pt={{ base: "130px", md: "80px", xl: "80px" }}
    >
      <MotionBox
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        w="100%"
      >
        <VStack spacing={6} align="stretch">
          {/* Breadcrumb */}
          <Breadcrumb
            spacing={2}
            separator={
              <Icon as={ChevronRightIcon} color={subTextColor} boxSize={3} />
            }
            fontSize="sm"
          >
            <BreadcrumbItem>
              <BreadcrumbLink
                onClick={() => navigate("/admin/payouts?tab=configuration")}
                color={themeColor}
                _hover={{ textDecoration: "underline" }}
                cursor="pointer"
              >
                Configurations
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbItem isCurrentPage>
              <BreadcrumbLink color={textColor}>History</BreadcrumbLink>
            </BreadcrumbItem>
          </Breadcrumb>

          {/* Header */}
          <Flex
            justify="space-between"
            align="center"
            direction={{ base: "column", md: "row" }}
            gap={4}
          >
            <HStack spacing={4} w={{ base: "100%", md: "auto" }}>
              <Button
                leftIcon={<Icon as={FaArrowLeft} />}
                onClick={() => navigate("/admin/payouts?tab=configuration")}
                variant="ghost"
                colorScheme="orange"
                size="sm"
              >
                Back
              </Button>
              <VStack align="start" spacing={0} px={{ base: 2, md: 0 }}>
                <Text
                  fontSize={{ base: "lg", md: "2xl" }}
                  fontWeight="bold"
                  color={textColor}
                >
                  Payout Configuration History
                </Text>
                <Text fontSize="sm" color={subTextColor}>
                  All configuration changes for this charger
                </Text>
              </VStack>
            </HStack>
          </Flex>

          {/* Content */}
          {loading ? (
            <GlassMorphismCard p={6}>
              <Center h="400px">
                <VStack spacing={4}>
                  <Spinner size="xl" color="orange.500" thickness="4px" />
                  <Text color={textColor}>Loading history...</Text>
                </VStack>
              </Center>
            </GlassMorphismCard>
          ) : history.length === 0 ? (
            <GlassMorphismCard p={6}>
              <Flex
                direction="column"
                align="center"
                justify="center"
                py={12}
                px={4}
              >
                <Icon as={FaHistory} boxSize={16} color={themeColor} mb={4} />
                <Text fontSize="xl" fontWeight="bold" mb={2} color={textColor}>
                  No History Found
                </Text>
                <Text color={subTextColor} textAlign="center" maxW="md">
                  There is no payout configuration history for this charger yet.
                </Text>
              </Flex>
            </GlassMorphismCard>
          ) : isDesktop ? (
            /* Desktop: Table view — mirrors LocationsTable */
            <GlassMorphismCard p={0}>
              {/* Header — title + year filter + count */}
              <Box p={6} borderBottom="1px solid" borderColor={borderColor}>
                <Flex justify="space-between" align="center" gap={4}>
                  <Text fontSize="xl" fontWeight="bold" color={textColor}>
                    History
                  </Text>
                  <HStack spacing={3}>
                    <Select
                      value={selectedYear}
                      onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                      size="sm"
                      borderRadius="md"
                      w="120px"
                      bg={inputBg}
                      _focus={{
                        borderColor: themeColor,
                        boxShadow: `0 0 0 1px ${themeColor}`,
                      }}
                    >
                      {YEAR_OPTIONS.map((year) => (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      ))}
                    </Select>
                    <Badge
                      colorScheme="orange"
                      borderRadius="full"
                      px={2}
                    >
                      {filteredHistory.length} total
                    </Badge>
                  </HStack>
                </Flex>
              </Box>

              {/* Table */}
              {filteredHistory.length === 0 ? (
                <Flex
                  direction="column"
                  align="center"
                  justify="center"
                  py={12}
                  px={4}
                >
                  <Icon as={FaHistory} boxSize={16} color={themeColor} mb={4} />
                  <Text fontSize="xl" fontWeight="bold" mb={2} color={textColor}>
                    No Matching Results
                  </Text>
                  <Text color={subTextColor} textAlign="center" maxW="md">
                    No history found for {selectedYear}.
                  </Text>
                </Flex>
              ) : (
                <>
                  <TableContainer>
                    <Table variant="simple" colorScheme="gray">
                      <Thead bg={theadBgColor}>
                        <Tr>
                          {[
                            "Period",
                            ...PAYOUT_CONFIG_FIELDS.map((f) => f.shortLabel),
                          ].map((h, i) => (
                            <Th
                              key={h}
                              borderColor={borderColor}
                              py={4}
                              px={6}
                              textTransform="uppercase"
                              fontSize="xs"
                              fontWeight="bold"
                              letterSpacing="wider"
                              color="gray.500"
                              textAlign={i === 0 ? "left" : "center"}
                              whiteSpace="nowrap"
                            >
                              {h}
                            </Th>
                          ))}
                        </Tr>
                      </Thead>
                      <Tbody>
                        {filteredHistory.map((entry, index) => {
                          const isCurrent =
                            entry.Month === currentMonth &&
                            entry.Year === currentYear;
                          return (
                            <MotionTr
                              key={entry.Id || index}
                              whileHover={{ backgroundColor: hoverBg }}
                              transition={{ duration: 0.2 }}
                              _hover={{ bg: hoverBg }}
                              bg={
                                isCurrent
                                  ? "rgba(245, 125, 54, 0.04)"
                                  : "transparent"
                              }
                            >
                              <Td
                                borderColor={borderColor}
                                py={4}
                                px={6}
                                fontSize="sm"
                              >
                                <HStack spacing={2}>
                                  <Text
                                    color={textColor}
                                    fontWeight="semibold"
                                    whiteSpace="nowrap"
                                  >
                                    {MONTH_NAMES[(entry.Month || 1) - 1]}
                                  </Text>
                                  {isCurrent && (
                                    <Badge
                                      bg={themeColor}
                                      color="white"
                                      borderRadius="full"
                                      px={2}
                                      py={0.5}
                                      fontSize="10px"
                                      fontWeight="bold"
                                    >
                                      Current
                                    </Badge>
                                  )}
                                </HStack>
                              </Td>
                              {PAYOUT_CONFIG_FIELDS.map((field) => (
                                <Td
                                  key={field.name}
                                  borderColor={borderColor}
                                  py={4}
                                  px={6}
                                  fontSize="sm"
                                  textAlign="center"
                                  color={textColor}
                                  whiteSpace="nowrap"
                                >
                                  {formatValue(field, entry[field.name])}
                                </Td>
                              ))}
                            </MotionTr>
                          );
                        })}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </GlassMorphismCard>
          ) : (
            /* Mobile/Tablet: Card grid view */
            <VStack spacing={4} align="stretch">
              {filteredHistory.map((entry, index) => {
                const isCurrent =
                  entry.Month === currentMonth && entry.Year === currentYear;
                return (
                  <GlassMorphismCard key={entry.Id || index} p={5}>
                    {/* Period header */}
                    <Flex justify="space-between" align="center" mb={4}>
                      <HStack spacing={2}>
                        <Icon as={FaHistory} color={themeColor} boxSize={4} />
                        <Text
                          color={textColor}
                          fontWeight="bold"
                          fontSize="md"
                        >
                          {MONTH_NAMES[(entry.Month || 1) - 1]}
                        </Text>
                      </HStack>
                      {isCurrent && (
                        <Badge
                          bg={themeColor}
                          color="white"
                          borderRadius="full"
                          px={3}
                          py={1}
                          fontSize="xs"
                          fontWeight="bold"
                        >
                          Current
                        </Badge>
                      )}
                    </Flex>

                    {/* Grouped fields */}
                    <VStack spacing={4} align="stretch">
                      {PAYOUT_CONFIG_GROUPS.map((group) => {
                        const groupFields = PAYOUT_CONFIG_FIELDS.filter(
                          (f) => f.group === group,
                        );
                        return (
                          <Box key={group}>
                            <Text
                              fontSize="10px"
                              fontWeight="bold"
                              textTransform="uppercase"
                              letterSpacing="wider"
                              color={themeColor}
                              mb={2}
                            >
                              {group}
                            </Text>
                            <SimpleGrid
                              columns={{ base: 2, sm: 3, md: 4 }}
                              spacing={3}
                            >
                              {groupFields.map((field) => (
                                <Box
                                  key={field.name}
                                  p={3}
                                  borderRadius="lg"
                                  bg={mobileFieldBg}
                                  border="1px solid"
                                  borderColor={cardBorderColor}
                                >
                                  <Text
                                    fontSize="11px"
                                    color={subTextColor}
                                    fontWeight="medium"
                                    mb={1}
                                    noOfLines={1}
                                  >
                                    {field.shortLabel}
                                  </Text>
                                  <Text
                                    fontSize="sm"
                                    fontWeight="bold"
                                    color={textColor}
                                  >
                                    {formatValue(field, entry[field.name])}
                                  </Text>
                                </Box>
                              ))}
                            </SimpleGrid>
                          </Box>
                        );
                      })}
                    </VStack>
                  </GlassMorphismCard>
                );
              })}
            </VStack>
          )}
        </VStack>
      </MotionBox>
    </Box>
  );
};

export default PayoutConfigHistory;
